import { isNaN, isObject, trimEnd, omitBy } from 'lodash-es'
import { Nullish } from 'utility-types'

import { APP_TITLE, THEME_NAME } from '$constants'

export type TypeAppTitle = {
  title?: string
  suffix?: string
  addSuffix?: boolean
  returnAppTitle?: boolean
}

export const getAppTitle = (options: TypeAppTitle = {}): string => {
  const {
    title,
    suffix = APP_TITLE,
    addSuffix = true,
    returnAppTitle = true
  } = options

  if (title === undefined && returnAppTitle) {
    return APP_TITLE
  }

  if (addSuffix) {
    return `${title} - ${suffix}`
  }

  return title || ''
}

export const noProtocolUrl = (url: string) => {
  const protocol_1 = 'http://'
  const protocol_2 = 'https://'

  const stripped = url
    .split(protocol_1)
    .filter(val => val !== protocol_1)
    .join('')
    .split(protocol_2)
    .filter(val => val !== protocol_2)
    .join('')

  return trimEnd(stripped, '/')
}

export const wait = (ms: number) => {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}

export const stripObject = <T extends object>(
  value: T,
  elements: unknown[] = [undefined]
) => {
  const cloned = { ...value }

  for (const name in cloned) {
    const val = cloned[name as never]

    if (isObject(val)) {
      cloned[name as never] = stripObject(val)
    }

    if (elements.includes(val)) {
      delete cloned[name as never]
    }
  }

  return cloned
}

export const isAllTrue = (values: boolean[]) => {
  return values.every(v => v)
}

export const getThemedData = <T>(data: Record<typeof THEME_NAME, T>) => {
  return data[THEME_NAME]
}

export const isNumeric = (value: unknown) => {
  const cond1 = isNaN(value)

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const cond2 = isNaN(parseFloat(value))

  return !cond1 && !cond2
}

export const filterValues = <Data extends object>(
  data: Data,
  exclusions: unknown[] = [undefined, '']
) => omitBy(data, value => exclusions.includes(value))

export const valueOrUndefined = <T>(
  determiner: boolean | Nullish,
  value: T
) => {
  return determiner ? value : undefined
}

export type PluralizedText = { singular: string; plural: string }
export const pluralize = (count: number, text: PluralizedText) => {
  return count > 1 ? text.plural : text.singular
}

export const asyncSuccessResolver = <
  TArgs extends unknown[],
  TResult,
  TResolved
>(
  fn: (...args: TArgs) => Promise<TResult>,
  resolver: (result: TResult) => TResolved
) => {
  return (...args: TArgs): Promise<TResolved> => fn(...args).then(resolver)
}

/*
 * @param maxRetries - The maximum number of retries. Default: 3.
 * @param delay - The delay between retries in milliseconds. Default: 1000.
 * @param currentAttempt - The current attempt number. Default: 1. This is only for internal tracking purposes.
 */
export const retryAsync = async <T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  delay = 2000,
  currentAttempt = 1
): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    if (maxRetries === 0) throw error

    const increasedDelay = delay * currentAttempt

    await wait(increasedDelay)

    const retriesLeft = maxRetries - 1
    const nextAttempt = currentAttempt + 1

    return retryAsync(fn, retriesLeft, delay, nextAttempt)
  }
}
